import { Link } from "react-router-dom";
import { MessageCircle, CalendarDays, Settings, FileText, Phone, Mail, ChevronRight } from "lucide-react";
import PageHero, { SplitSection } from "@/components/site/PageHero";
import FeatureStrip from "@/components/site/FeatureStrip";
import Faq from "@/components/site/Faq";
import { Btn, Reveal } from "@/components/site/Reveal";
import { BRAND } from "@/content";
import { scrollToId } from "@/lib/scroll";

const HELP = [
  { icon: MessageCircle, title: "Get Answers", text: "We're here to answer any questions you have." },
  { icon: CalendarDays, title: "Schedule a Visit", text: "Book a free in-home water test." },
  { icon: Settings, title: "System Support", text: "Get help with your current system." },
  { icon: FileText, title: "Product Info", text: "Learn more about our equipment and options." },
];

const CARDS = [
  { icon: Phone, title: "Call Us", text: "Speak directly with our team.", href: BRAND.phoneHref, id: "call" },
  { icon: Mail, title: "Email Us", text: "Send us a message anytime.", href: `mailto:${BRAND.email}`, id: "email" },
  { icon: CalendarDays, title: "Schedule a Free Water Test", text: "Book a convenient time for an in-home visit.", to: "/contact", id: "schedule" },
];

const TOPICS = [
  { title: "General Questions", text: "Learn more about our systems, process, and pricing.", hash: "faq" },
  { title: "Water Testing", text: "Schedule a free in-home water test.", to: "/signup" },
  { title: "Installation & Service", text: "Get support with installation or your current system.", to: "/how-it-works" },
  { title: "Product Information", text: "Find the right system for your home.", to: "/products" },
];

const Row = ({ c }) => {
  const inner = (
    <>
      <c.icon className="h-9 w-9 shrink-0" strokeWidth={1.3} />
      <span className="flex-1"><span className="block font-head font-semibold text-[17px]">{c.title}</span><span className="block text-[14px] text-[var(--body)]">{c.text}</span></span>
      <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  const cls = "group flex items-center gap-6 bg-[var(--soft)] px-6 py-5 hover:bg-[#EEEEEE] transition-colors";
  return c.to ? <Link to={c.to} data-testid={`contact-card-${c.id}`} className={cls}>{inner}</Link> : <a href={c.href} data-testid={`contact-card-${c.id}`} className={cls}>{inner}</a>;
};

const Topic = ({ t, i }) => {
  const inner = (
    <>
      <span className="flex-1"><span className="block font-head font-semibold text-[16px]">{t.title}</span><span className="block text-[14px] text-[var(--body)]">{t.text}</span></span>
      <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  const cls = "group w-full text-left flex items-center gap-4 py-4 border-b border-[#D4D4D4] last:border-b-0";
  return t.to ? <Link to={t.to} data-testid={`help-topic-${i}`} className={cls}>{inner}</Link> : <button onClick={() => scrollToId(t.hash)} data-testid={`help-topic-${i}`} className={cls}>{inner}</button>;
};

export default function Faqs() {
  return (
    <>
      <PageHero testId="faqs-hero" eyebrow="We Are Here to Help" lines={["Questions?", "We got you."]} text="Whether you're looking for more information, want to schedule a free water test, or need support with your system, our team is here to help. We make it easy to get the answers you need and find the right solution for your home." cta="Get in Touch" ctaOnClick={() => scrollToId("reach-out")} img="/img/tech-tablet.jpg" alt="Water System Bros. technician explaining results on a tablet" />
      <FeatureStrip items={HELP} testId="faqs-help-strip" className="!border-t-0" />
      <section id="reach-out" data-testid="reach-out" className="bg-white py-4 lg:py-8">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="relative aspect-[5/4] overflow-hidden"><img src="/img/tech-door.jpg" alt="Technician heading to a customer's door" className="absolute inset-0 h-full w-full object-cover" /></Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow">Reach Out</p>
            <h2 className="headline mt-4 text-4xl sm:text-5xl">Contact us</h2>
            <p className="mt-4 mb-6 text-[17px] text-[var(--body)]">Our team is ready to help. Choose the best way to get in touch and we'll get back to you as soon as possible.</p>
            <div className="space-y-3">{CARDS.map((c) => <Row key={c.id} c={c} />)}</div>
          </Reveal>
        </div>
      </section>
      <section data-testid="how-can-we-help" className="bg-[var(--soft)] py-16 lg:py-20 mt-12">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal><p className="eyebrow">Common Questions</p><h2 className="headline mt-4 text-4xl sm:text-6xl">How can<br />we help?</h2><p className="mt-5 text-[17px] text-[var(--body)] max-w-sm">Here are some of the most common reasons homeowners reach out to us.</p></Reveal>
          <Reveal delay={0.1} className="lg:border-l border-[#D4D4D4] lg:pl-10">{TOPICS.map((t, i) => <Topic key={t.title} t={t} i={i} />)}</Reveal>
        </div>
      </section>
      <Faq />
      <SplitSection testId="faqs-still" reverse eyebrow="Still Have Questions?" title={<>We're here<br />to help.</>} text="Clean, better-tasting water for your home is just a conversation away. Reach out today and our team will guide you every step of the way." img="/img/house.jpg" alt="Modern home with palm trees">
        <Btn to="/contact" testId="faqs-contact-btn" className="mt-8 self-start">Contact Us</Btn>
      </SplitSection>
    </>
  );
}
